import type { TerminalKind } from './terminal';

/**
 * Kitchen / Bar displays with one or more stations (ADR-045). A display is assigned a set of production stations and
 * shows one tab per station: a station on the Bar route under the Bar tab, any other under the Kitchen tab (the rule of
 * the server's landing). The page's route chooses the tab; the server decides what the display may act on, so nothing
 * here grants anything: it only chooses what to show.
 */
export type KdsTab = 'KITCHEN' | 'BAR';

/** Navigation order of the tabs. */
export const KDS_TABS: readonly KdsTab[] = ['KITCHEN', 'BAR'];
export const KDS_TAB_PATH: Record<KdsTab, '/kitchen' | '/bar'> = {
  KITCHEN: '/kitchen',
  BAR: '/bar',
};
export const KDS_TAB_LABEL: Record<KdsTab, string> = { KITCHEN: 'Kitchen', BAR: 'Bar' };

/** A station as `me()` gives it to a display. */
export interface KdsStation {
  id: string;
  code: string;
  name: string;
  route_code: string | null;
}

/** The part of `me().terminal` these helpers read. */
export interface KdsTerminal {
  kind: TerminalKind;
  /** The display's home station: the tab it opens on, chosen by the owner. */
  production_station_id: string | null;
  station_route_code: string | null;
  /** Assigned stations (ADR-045); absent from a server older than ADR-045. */
  stations?: KdsStation[] | null;
}

/** The tab a station shows under: Bar for the BAR route, Kitchen for every other route. */
export function kdsTabOf(routeCode: string | null | undefined): KdsTab {
  return routeCode?.toUpperCase() === 'BAR' ? 'BAR' : 'KITCHEN';
}

/**
 * A display's assigned stations, in the server's order; [] for any other kind of terminal. A server older than ADR-045
 * sends no list: the home station alone is then the display's station, as it was.
 */
export function assignedStations(terminal: KdsTerminal | null | undefined): KdsStation[] {
  if (!terminal || terminal.kind !== 'kds') return [];
  if (Array.isArray(terminal.stations)) return terminal.stations;
  if (!terminal.production_station_id) return [];
  return [
    {
      id: terminal.production_station_id,
      code: '',
      name: '',
      route_code: terminal.station_route_code,
    },
  ];
}

/** The tabs a terminal shows, in navigation order; null when it is not a Kitchen / Bar display. */
export function kdsTabsFor(terminal: KdsTerminal | null | undefined): KdsTab[] | null {
  if (!terminal || terminal.kind !== 'kds') return null;
  const tabs = new Set(assignedStations(terminal).map((s) => kdsTabOf(s.route_code)));
  return KDS_TABS.filter((t) => tabs.has(t));
}

/** The stations of a display that show under one tab. */
export function stationsOnTab(terminal: KdsTerminal | null | undefined, tab: KdsTab): KdsStation[] {
  return assignedStations(terminal).filter((s) => kdsTabOf(s.route_code) === tab);
}

export type DeviceStation =
  /** Show this station; `choices` are the display's stations on this tab (a picker when there are several). */
  | { kind: 'station'; stationId: string; choices: KdsStation[] }
  /** The display has stations, none on this tab: say so, and offer the tab it opens on. */
  | { kind: 'not_assigned'; tab: KdsTab; openTab: KdsTab; assigned: KdsStation[] }
  /** Not a Kitchen / Bar display, or one with no station. */
  | { kind: 'unbound' };

/**
 * Which station a display shows on a tab. The tab comes from the page's route; a station is shown only when it is
 * assigned to the display and belongs to that tab, so the Bar tab never shows a Kitchen station. On a tab with
 * several of its stations: the one picked on this tab, else the home station when it is one of them, else the first.
 */
export function resolveDeviceStation(
  terminal: KdsTerminal | null | undefined,
  tab: KdsTab,
  choice: string | null,
): DeviceStation {
  const assigned = assignedStations(terminal);
  if (assigned.length === 0) return { kind: 'unbound' };
  const onTab = assigned.filter((s) => kdsTabOf(s.route_code) === tab);
  if (onTab.length === 0) {
    const home = assigned.find((s) => s.id === terminal?.production_station_id) ?? assigned[0]!;
    return { kind: 'not_assigned', tab, openTab: kdsTabOf(home.route_code), assigned };
  }
  const picked =
    onTab.find((s) => s.id === choice) ??
    onTab.find((s) => s.id === terminal?.production_station_id) ??
    onTab[0]!;
  return { kind: 'station', stationId: picked.id, choices: onTab };
}

/** The display's other tabs and their stations, for the waiting count shown on the open tab. */
export function otherTabs(
  terminal: KdsTerminal | null | undefined,
  tab: KdsTab,
): Array<{ tab: KdsTab; stationIds: string[] }> {
  return KDS_TABS.filter((t) => t !== tab)
    .map((t) => ({ tab: t, stationIds: stationsOnTab(terminal, t).map((s) => s.id) }))
    .filter((t) => t.stationIds.length > 0);
}

/**
 * Which tickets (or cancelled items) a display has already announced, per station, for the life of the page. Every
 * part of the screen that reads a station — its board, or the waiting count of its tab while another tab is open —
 * asks the same registry, so a ticket chimes once whichever part sees it first, and never again: not on a refetch, a
 * reconnect, a failed read that keeps older data, or a tab switch that remounts the board. The first look at a station
 * records what is already there without chiming (opening a screen does not announce its backlog).
 */
export interface HeardRegistry {
  /** The ids not announced before for this key (e.g. `ticket:<station id>`), and records them. */
  fresh(key: string, ids: readonly string[]): string[];
}

export function createHeardRegistry(): HeardRegistry {
  const heard = new Map<string, Set<string>>();
  return {
    fresh(key, ids) {
      const known = heard.get(key);
      if (!known) {
        heard.set(key, new Set(ids));
        return [];
      }
      const out = ids.filter((id) => !known.has(id));
      for (const id of out) known.add(id);
      return out;
    },
  };
}
